'use strict';

var React = require('react');
var reactUtils = require('@sravni/react-utils');
var Sheet_module = require('../styles/Sheet.module.less.js');
var SheetContext = require('./SheetContext.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

/**
 * @description Sheet Content component.
 *
 * @component
 * @example
 * ```jsx
 * <Sheet.Content>content</Sheet>
 * ```
 */
const Content = React__namespace.forwardRef((props, ref) => {
    const { className, children, ...restProps } = props;
    const { setDragElastic } = React__namespace.useContext(SheetContext.default);
    const containerRef = React__namespace.useRef(null);
    const allowUp = React__namespace.useRef(false);
    const allowDown = React__namespace.useRef(false);
    const prevPageY = React__namespace.useRef(0);
    const prevPageX = React__namespace.useRef(0);
    const [dividerVisible, setDividerVisible] = React__namespace.useState(false);
    const cx = reactUtils.useStyles(Sheet_module.default);
    const classNames = cx(className, 'content', {
        divider: dividerVisible,
    });
    const updateDivider = React__namespace.useCallback(() => {
        const container = containerRef?.current;
        if (!container)
            return;
        setDividerVisible(container.scrollHeight > container.clientHeight);
    }, [containerRef]);
    const handleTouchStart = React__namespace.useCallback((event) => {
        const container = containerRef.current;
        if (!container)
            return;
        allowUp.current = container.scrollTop > 0;
        allowDown.current = container.scrollTop < container.scrollHeight - container.clientHeight;
        prevPageY.current = event.touches[0].pageY;
        prevPageX.current = event.touches[0].pageX;
        if (setDragElastic) {
            allowUp.current && !allowDown.current && setDragElastic({ top: 0.5, bottom: 0 });
            !allowUp.current && allowDown.current && setDragElastic({ top: 0, bottom: 0.5 });
            allowUp.current && allowDown.current && setDragElastic({ top: 0, bottom: 0 });
        }
    }, [setDragElastic]);
    const handleTouchMove = React__namespace.useCallback((event) => {
        const moveUp = event.touches[0].pageY > prevPageY.current;
        const moveDown = event.touches[0].pageY < prevPageY.current;
        const horizontalMove = event.touches[0].pageX > prevPageX.current || event.touches[0].pageX < prevPageX.current;
        prevPageY.current = event.touches[0].pageY;
        prevPageX.current = event.touches[0].pageX;
        if ((moveUp && allowUp.current) || (moveDown && allowDown.current)) {
            event.stopPropagation();
            return;
        }
        if (horizontalMove && !moveUp && !moveDown)
            return;
        event.preventDefault();
    }, [prevPageY, prevPageX, allowUp, allowDown]);
    const stopPropagationPointerDown = React__namespace.useCallback((event) => event.stopPropagation(), []);
    React__namespace.useEffect(() => {
        updateDivider();
        window.addEventListener('resize', updateDivider);
        return () => window.removeEventListener('resize', updateDivider);
    }, [updateDivider]);
    React__namespace.useEffect(() => {
        const container = containerRef.current;
        container?.addEventListener('touchstart', handleTouchStart);
        container?.addEventListener('touchmove', handleTouchMove);
        return () => {
            container?.removeEventListener('touchstart', handleTouchStart);
            container?.removeEventListener('touchmove', handleTouchMove);
        };
    }, [handleTouchStart, handleTouchMove]);
    return (React__namespace.createElement("div", { ...restProps, ref: reactUtils.useMergedRef(ref, containerRef), className: classNames, onPointerDown: stopPropagationPointerDown }, children));
});
Content.defaultProps = {};
Content.displayName = 'Content';

exports.Content = Content;
//# sourceMappingURL=Content.js.map
